import { Injectable } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { TransportOrdersStateService } from './transport-orders-state.service';
import { LoggerService } from '../core/services/logger.service';

@Injectable({
  providedIn: 'root'
})
export class TransportOrdersNavigationService {

  constructor(
    private router: Router,
    private stateService: TransportOrdersStateService,
    private logger: LoggerService
  ) { }

  /**
   * Navigate to details preserving state
   */
  navigateToDetail(id: number): void {
    const currentState = this.stateService.getCurrentState();
    const queryParams = this.stateService.stateToQueryParams(currentState);

    this.logger.info(
      `Navegando a detalle OT ${id} con estado conservado`,
      'TransportOrdersNavigationService'
    );

    this.router.navigate(['/ordenes-transporte-agencia', id], {
      queryParams
    });
  }

  /** Abre el detalle en una nueva pestaña (misma URL y query params que navigateToDetail). */
  openDetailInNewTab(id: number): void {
    const currentState = this.stateService.getCurrentState();
    const queryParams = this.stateService.stateToQueryParams(currentState);

    this.logger.info(
      `Abriendo detalle OT ${id} en nueva pestaña con estado conservado`,
      'TransportOrdersNavigationService'
    );

    const urlTree = this.router.createUrlTree(['/ordenes-transporte-agencia', id], {
      queryParams
    });
    const url = this.router.serializeUrl(urlTree);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Back to list preserving state
   */
  backToList(): void {
    const currentState = this.stateService.getCurrentState();
    const queryParams = this.stateService.stateToQueryParams(currentState);

    this.logger.info(
      'Regresando al listado de OT con estado conservado',
      'TransportOrdersNavigationService'
    );

    this.router.navigate(['/ordenes-transporte-agencia'], {
      queryParams
    });
  }

  /**
   * Apply filters and update URL
   */
  applyFilters(): void {
    const currentState = this.stateService.getCurrentState();
    const queryParams = this.stateService.stateToQueryParams(currentState);

    this.logger.debug(
      `Aplicando filtros OT y actualizando URL: ${JSON.stringify(queryParams)}`,
      'TransportOrdersNavigationService'
    );

    this.router.navigate([], {
      relativeTo: this.getActivatedRoute(),
      queryParams,
      queryParamsHandling: 'replace'
    });
  }

  /**
   * Load state from URL
   */
  loadStateFromUrl(route: ActivatedRoute): boolean {
    const queryParams = route.snapshot.queryParams;

    if (this.hasValidQueryParams(queryParams)) {
      const state = this.stateService.queryParamsToState(queryParams);

      this.logger.info(
        `Cargando estado OT desde URL: ${JSON.stringify(queryParams)}`,
        'TransportOrdersNavigationService'
      );

      this.stateService.updateState(state);
      return true;
    }

    return false;
  }

  /**
   * Clear filters and URL
   */
  clearFilters(): void {
    this.stateService.clearState();

    this.logger.info(
      'Limpiando filtros OT y URL',
      'TransportOrdersNavigationService'
    );

    this.router.navigate([], {
      relativeTo: this.getActivatedRoute(),
      queryParams: {},
      queryParamsHandling: 'replace'
    });
  }

  private hasValidQueryParams(params: any): boolean {
    const validKeys = [
      'clienteId', 'exportadorId', 'nave', 'temporadaId',
      'dateFrom', 'dateTo', 'referenciaCliente', 'contenedor',
      'dus', 'nroLiquidacion', 'booking', 'licensePlate', 'page', 'limit',
      'orderBy', 'orderDirection'
    ];

    return validKeys.some(key =>
      Object.prototype.hasOwnProperty.call(params, key) &&
      params[key] !== null &&
      params[key] !== ''
    );
  }

  private getActivatedRoute(): ActivatedRoute {
    let route = this.router.routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }
}
